// src/pages/admin/AdminClinicsPage.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InlineLoader } from "components/ui/Loader";
import { getErrorMessage } from "utils/errors";
import {
  adminListClinics,
  adminCreateClinic,
  adminUploadClinicsCSV,
  adminSyncClinicCoords,
  adminPublishClinic,
  adminArchiveClinic,
  adminDeleteClinic,
  type AdminClinic,
  type UploadCSVResult,
  type SyncCoordsResult
} from "api/adminClinics";

import SingleClinicForm, { SingleClinicFormValues } from "features/admin/components/SingleClinicForm";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const PAGE_SIZE = 25;
const GOLD = "#f3dfae";

const AdminClinicsPage: React.FC = () => {
  const [items, setItems] = useState<AdminClinic[] | null>(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string>("");

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string>("");

  const [page, setPage] = useState(1);
  const total = items?.length ?? 0;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const pageSlice = useMemo(() => {
    if (!items) return [];
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const refresh = async () => {
    setErr("");
    setLoading(true);
    try {
      const rows = await adminListClinics();
      setItems(rows || []);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load clinics"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const pageEl = pageRef.current;
    const titleEl = titleRef.current;
    const containerEl = containerRef.current;
    if (!pageEl || !titleEl || !containerEl) return;

    const compute = () => {
      const p = pageEl.getBoundingClientRect();
      const t = titleEl.getBoundingClientRect();
      const c = containerEl.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(pageEl);
    ro.observe(titleEl);
    ro.observe(containerEl);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [total, page, loading, err]);

  const handleCreate = async (values: SingleClinicFormValues) => {
    setErr("");
    try {
      await adminCreateClinic(values);
      await refresh();
    } catch (e: any) {
      throw new Error(getErrorMessage(e, "Failed to create clinic"));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setUploadMsg("");
    if (!file) return;

    setUploading(true);
    try {
      const res: UploadCSVResult = await adminUploadClinicsCSV(file);
      const bits: string[] = [];
      if (res.added !== undefined) bits.push(`added ${res.added}`);
      if (res.updated !== undefined) bits.push(`updated ${res.updated}`);
      if (res.failed !== undefined) bits.push(`failed ${res.failed}`);
      setUploadMsg(bits.length ? `Upload complete: ${bits.join(", ")}.` : "Upload complete.");
      await refresh();
      setFile(null);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to upload CSV"));
    } finally {
      setUploading(false);
    }
  };

  const handleSync = async () => {
    setErr("");
    setSyncMsg("");
    setSyncing(true);
    try {
      const res: SyncCoordsResult = await adminSyncClinicCoords();
      const msg = [
        `Updated: ${res.updated}`,
        res.skipped !== undefined ? `Skipped: ${res.skipped}` : undefined,
        res.errors?.length ? `Errors: ${res.errors.length}` : undefined
      ]
        .filter(Boolean)
        .join(" · ");
      setSyncMsg(msg || "Sync complete.");
      await refresh();
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to sync coordinates"));
    } finally {
      setSyncing(false);
    }
  };

  const handlePublish = async (clinic: AdminClinic) => {
    setErr("");
    try {
      const updated = await adminPublishClinic(clinic.id, clinic.lock_version ?? 0);
      setItems((prev) => (prev ? prev.map((c) => (c.id === clinic.id ? updated : c)) : prev));
    } catch (e: any) {
      setErr(getErrorMessage(e, `Failed to publish ${clinic.name}`));
    }
  };

  const handleArchive = async (clinic: AdminClinic) => {
    setErr("");
    try {
      const updated = await adminArchiveClinic(clinic.id, clinic.lock_version ?? 0);
      setItems((prev) => (prev ? prev.map((c) => (c.id === clinic.id ? updated : c)) : prev));
    } catch (e: any) {
      setErr(getErrorMessage(e, `Failed to archive ${clinic.name}`));
    }
  };

  const handleDelete = async (clinic: AdminClinic) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${clinic.name}"?`)) return;
    setErr("");
    try {
      await adminDeleteClinic(clinic.id);
      await refresh();
    } catch (e: any) {
      setErr(getErrorMessage(e, `Failed to delete ${clinic.name}`));
    }
  };

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin · Clinics
          </h1>
        </header>

        {/* Main container (full width, no md:w-3/5 constraint) */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            {/* Top row */}
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Admin Home
              </Link>

              <button
                type="button"
                onClick={refresh}
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "border border-slate-200 bg-white",
                  "px-4 py-2",
                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                  "hover:bg-slate-50 transition-colors font-ui font-bold"
                ].join(" ")}
              >
                Refresh
              </button>
            </div>

            {/* Error */}
            {err && (
              <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
                {err}
              </div>
            )}

            {/* Create clinic */}
            <div className="mt-8">
              <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">
                Create clinic
              </h2>
              <div className="mt-4">
                <SingleClinicForm onCreate={handleCreate} />
              </div>
            </div>

            {/* Bulk tools */}
            <section className="mt-8 rounded-2xl border p-5" style={{ borderColor: `${GOLD}B3` }}>
              <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">
                Bulk upload &amp; coordinate sync
              </h2>

              <form
                onSubmit={handleUpload}
                className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                  className="text-sm border rounded-xl p-2.5 flex-1"
                />

                <button
                  type="submit"
                  disabled={uploading || !file}
                  className={[
                    "inline-flex items-center justify-center rounded-full",
                    "bg-[#f3dfae] px-5 py-2",
                    "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                    "hover:opacity-90 transition-opacity font-ui font-bold",
                    "disabled:opacity-50"
                  ].join(" ")}
                >
                  {uploading ? "Uploading…" : "Upload CSV"}
                </button>

                <button
                  type="button"
                  onClick={handleSync}
                  disabled={syncing}
                  className={[
                    "inline-flex items-center justify-center rounded-full",
                    "border px-5 py-2",
                    "text-xs tracking-[0.18em] uppercase font-ui font-bold",
                    "hover:opacity-90 transition-opacity",
                    "disabled:opacity-50"
                  ].join(" ")}
                  style={{ borderColor: GOLD, color: "#151A3A" }}
                >
                  {syncing ? "Syncing…" : "Sync coordinates"}
                </button>
              </form>

              {(uploadMsg || syncMsg) && (
                <div className="mt-3 text-sm text-slate-700 font-body">
                  {uploadMsg && <div>Upload: {uploadMsg}</div>}
                  {syncMsg && <div>Sync: {syncMsg}</div>}
                </div>
              )}

              <div className="mt-3 font-ui text-xs uppercase tracking-wide text-slate-500">
                CSV columns typically include: name, slug, short_description, long_description, city,
                country (ISO-2), postcode, website_url, phone, email. The server validates and geocodes.
              </div>
            </section>

            {/* Clinics list */}
            <section className="mt-10">
              <div className="flex items-start justify-between gap-4">
                <h2 className="font-ui font-bold uppercase tracking-wide text-slate-800">Clinics</h2>
                <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                  Page {page} of {totalPages} · total {total}
                </div>
              </div>

              <div className="mt-4">
                {loading ? (
                  <InlineLoader label="Loading…" />
                ) : !items || items.length === 0 ? (
                  <div className="font-body text-slate-600">No clinics found.</div>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr className="text-slate-700 font-ui text-xs uppercase tracking-wide">
                            <th className="text-left p-3">Name</th>
                            <th className="text-left p-3">Slug</th>
                            <th className="text-left p-3">City</th>
                            <th className="text-left p-3">Country</th>
                            <th className="text-left p-3">Postcode</th>
                            <th className="text-left p-3">Lat</th>
                            <th className="text-left p-3">Lng</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageSlice.map((c) => {
                            const status = (c.status ?? "draft").toLowerCase();
                            return (
                              <tr key={c.id} className="border-t border-slate-200">
                                <td className="p-3 font-body text-slate-900">{c.name}</td>
                                <td className="p-3 font-body text-slate-700">{c.slug}</td>
                                <td className="p-3 font-body text-slate-700">{c.city ?? "—"}</td>
                                <td className="p-3 font-body text-slate-700">{c.country ?? "—"}</td>
                                <td className="p-3 font-body text-slate-700">{c.postcode ?? "—"}</td>
                                <td className="p-3 font-body text-slate-700">
                                  {typeof c.latitude === "number" ? c.latitude.toFixed(6) : "—"}
                                </td>
                                <td className="p-3 font-body text-slate-700">
                                  {typeof c.longitude === "number" ? c.longitude.toFixed(6) : "—"}
                                </td>
                                <td className="p-3">
                                  <span
                                    className={[
                                      "inline-flex items-center rounded-full px-3 py-1",
                                      "font-ui text-xs uppercase tracking-wide border",
                                      status === "published"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : status === "archived"
                                        ? "bg-slate-100 text-slate-700 border-slate-200"
                                        : "bg-amber-50 text-amber-800 border-amber-200"
                                    ].join(" ")}
                                  >
                                    {status}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div className="flex flex-wrap gap-2">
                                    {status === "draft" && (
                                      <button
                                        type="button"
                                        onClick={() => handlePublish(c)}
                                        className={[
                                          "inline-flex items-center justify-center rounded-full",
                                          "bg-[#f3dfae] px-4 py-2",
                                          "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                          "hover:opacity-90 transition-opacity font-ui font-bold"
                                        ].join(" ")}
                                      >
                                        Publish
                                      </button>
                                    )}

                                    {status === "published" && (
                                      <button
                                        type="button"
                                        onClick={() => handleArchive(c)}
                                        className={[
                                          "inline-flex items-center justify-center rounded-full",
                                          "border px-4 py-2",
                                          "text-xs tracking-[0.18em] uppercase font-ui font-bold",
                                          "hover:opacity-90 transition-opacity"
                                        ].join(" ")}
                                        style={{ borderColor: GOLD, color: "#151A3A" }}
                                      >
                                        Archive
                                      </button>
                                    )}

                                    {status === "archived" && (
                                      <button
                                        type="button"
                                        onClick={() => handlePublish(c)}
                                        className={[
                                          "inline-flex items-center justify-center rounded-full",
                                          "bg-[#f3dfae] px-4 py-2",
                                          "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                          "hover:opacity-90 transition-opacity font-ui font-bold"
                                        ].join(" ")}
                                      >
                                        Unarchive
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() => handleDelete(c)}
                                      className={[
                                        "inline-flex items-center justify-center rounded-full",
                                        "px-4 py-2",
                                        "border border-red-200 bg-red-50 text-red-700",
                                        "text-xs tracking-[0.18em] uppercase font-ui font-bold",
                                        "hover:bg-red-100 transition-colors"
                                      ].join(" ")}
                                      >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className={[
                            "inline-flex items-center justify-center rounded-full",
                            "border border-slate-200 bg-white",
                            "px-4 py-2",
                            "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                            "hover:bg-slate-50 transition-colors font-ui font-bold",
                            "disabled:opacity-50"
                          ].join(" ")}
                        >
                          Prev
                        </button>

                        <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                          Page {page} / {totalPages}
                        </div>

                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className={[
                            "inline-flex items-center justify-center rounded-full",
                            "border border-slate-200 bg-white",
                            "px-4 py-2",
                            "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                            "hover:bg-slate-50 transition-colors font-ui font-bold",
                            "disabled:opacity-50"
                          ].join(" ")}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AdminClinicsPage;
